import { Group, Stack, UnstyledButton } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Divider } from "#components/atoms/Divider/Divider";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { SearchBar } from "#components/atoms/SearchBar/SearchBar";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";
import { Page } from "#components/templates/Page/Page";
import { SearchPagination } from "#modules/search/view/SearchPagination";
import { SearchResultsList } from "#modules/search/view/SearchResults";
import { useDeleteRecentSearch } from "#modules/search/view/useDeleteRecentSearch";
import { SearchFilter, useSearchPageParams } from "#modules/search/view/useSearchPageParams";
import { useSearchPageQueries } from "#modules/search/view/useSearchPageQueries";
import { Icon } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { SearchEntity } from "shared";

import classes from "./SearchPage.module.css";

const FILTERS = [
  { value: "all", labelKey: "searchPage.tabs.all" },
  { value: SearchEntity.QuoteRequest, labelKey: "searchPage.tabs.quoteRequests" },
  { value: SearchEntity.QuoteRequestLine, labelKey: "searchPage.tabs.parts" },
] as const satisfies readonly { value: SearchFilter; labelKey: string }[];

const filterButtonStyle = (isSelected: boolean): React.CSSProperties => ({
  borderRadius: radius.lg,
  padding: `${spacing[2.5]} ${spacing[4]}`,
  backgroundColor: isSelected ? colors.bg.elevation.tertiary : colors.bg.elevation.primary,
  border: isSelected
    ? `1.5px solid ${colors.icon.primaryDefault}`
    : `1px solid ${colors.border.tertiary}`,
});

const RecentSearches = ({
  searches,
  onSelect,
}: {
  searches: string[];
  onSelect: (query: string) => void;
}) => {
  const { t } = useTranslation();
  const { deleteRecentSearch } = useDeleteRecentSearch();

  if (searches.length === 0) {
    return null;
  }

  return (
    <Stack gap={spacing[2.5]}>
      <Text size="small" type="medium">
        {t("searchPage.recent")}
      </Text>
      <Box>
        {searches.map((search, index) => (
          <Box key={`${search}-${index}`}>
            <Divider type="secondary" />
            <Box className={classes.row}>
              <UnstyledButton
                className={classes.label}
                onClick={() => onSelect(search)}
                py={spacing[2.5]}
              >
                <Group gap={spacing[2]} wrap="nowrap">
                  <Icon name="history" size={16} color={colors.icon.secondary} />
                  <Text size="small" type="regular" truncate>
                    {search}
                  </Text>
                </Group>
              </UnstyledButton>
              <IconButton
                size="sm"
                variant="custom"
                aria-label={t("searchPage.removeRecent", { query: search })}
                onClick={() => deleteRecentSearch(search)}
                classNames={{ root: classes.deleteButton }}
              >
                <Icon name="x" size={16} />
              </IconButton>
            </Box>
          </Box>
        ))}
        {searches.length > 0 ? <Divider type="secondary" /> : null}
      </Box>
    </Stack>
  );
};

export const SearchPage = () => {
  const { t } = useTranslation();
  const { query, filter, page, submitQuery, setFilter, setPage } = useSearchPageParams();
  const [draft, setDraft] = useState(query);
  const entity = filter === "all" ? undefined : filter;

  useEffect(() => {
    setDraft(query);
  }, [query]);

  const { results, isSearchError, recentSearches, pageCount } = useSearchPageQueries({
    query,
    entity,
    page,
  });
  const hasSubmittedQuery = query.length > 0;

  return (
    <Page>
      <Page.Header />
      <Page.Content>
        <Stack gap={0} style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Stack gap={spacing[4]} pt={spacing[4]} pb={spacing[8]} maw={560}>
            <Title size="small">{t("searchPage.title")}</Title>
            <SearchBar
              size="large"
              value={draft}
              onChange={setDraft}
              onSearch={() => submitQuery(draft)}
              onClear={() => {
                setDraft("");
                submitQuery("");
              }}
              placeholder={t("searchPage.placeholder")}
              autoFocus
            />
            <Group gap={spacing[2]}>
              {FILTERS.map((item) => {
                const isSelected = filter === item.value;
                return (
                  <UnstyledButton
                    key={item.value}
                    onClick={() => setFilter(item.value)}
                    aria-pressed={isSelected}
                    style={filterButtonStyle(isSelected)}
                  >
                    <Text size="tiny" type="medium">
                      {t(item.labelKey)}
                    </Text>
                  </UnstyledButton>
                );
              })}
            </Group>
          </Stack>
          {hasSubmittedQuery ? (
            <Stack gap={spacing[2.5]}>
              {isSearchError ? (
                <Text size="small" type="regular" color={colors.text.secondaryDefault}>
                  {t("searchPage.error")}
                </Text>
              ) : null}
              {results ? (
                <>
                  <Text
                    size="small"
                    type="regular"
                    color={colors.text.secondaryDefault}
                    style={{ paddingBottom: spacing[2.5] }}
                  >
                    {t("searchPage.results", { count: results.total })}
                  </Text>
                  <SearchResultsList results={results.data} />
                </>
              ) : null}
              {pageCount > 1 ? (
                <SearchPagination page={page} pageCount={pageCount} onPageChange={setPage} />
              ) : null}
            </Stack>
          ) : (
            <RecentSearches searches={recentSearches} onSelect={submitQuery} />
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
};
